import parseISO from 'date-fns/parseISO';
import createCachedSelector from 're-reselect';
import {createSelector} from 'reselect';
import {RootState} from '../../rootReducer';
import {meetingsAdapter, suggestionsAdapter, timeZonesAdapter} from './adapters';
import {dateCompareAsc, dateCompareDesc, formatDateAsKey} from '@lib/utils';
import {filterMeetingsByDate, findAttendeeByDirectoryObjectId, isHybridMeetingsFeatureEnabled} from './helpers';

import {getUserDirectoryObjectId} from '../user/selectors';
import {groupAndSortAttendeesByLocationType, isMeetingDateTimeRangeValid} from './utils';
import {getIsHybridMeetingsFeatureEnabled, getIsOccupancyEnabled} from '../../infrastructure';

export const getMeetingPreferences = (state: RootState) => state.data.meetings.userPreferences;

export const getMeetingPreferencesLoadingStatus = (state: RootState) =>
  state.data.meetings.meetingPreferencesLoadingStatus;

export const getIsMeetingsCalendarEnabled = (state: RootState) =>
  state.data.meetings.settings.enabledCalendarIntegration;

export const getIsGoogleIntegrationActive = (state: RootState) => state.data.meetings.settings.enabledGoogleWorkspace;

export const getIsMicrosoft365IntegrationActive = (state: RootState) => state.data.meetings.settings.enabledOffice365;

export const getIsOnlineMeetingLinkSupported = (state: RootState) =>
  state.data.meetings.settings.onlineMeetingLinkSupported;

export const getMeetingsSettings = (state: RootState) => state.data.meetings.settings;

export const getHasUserGivenCalendarConsent = (state: RootState) => state.data.meetings.userPreferences.enableCalendar;

export const getIsUserCalendarConsentInvalid = (state: RootState) => state.data.meetings.isUserCalendarConsentInvalid;

export const getSelectedMeetingId = (state: RootState) => state.data.meetings.selectedMeetingId;

export const getMeetingPreferencesWorkingHours = (state: RootState) => state.data.meetings.userPreferences.workingHours;

export const getMeetingSuggestionLoadingStatus = (state: RootState) =>
  state.data.meetings.meetingSuggestionLoadingStatus;

export const getIsUpdatingMeetingUserPreferences = (state: RootState) =>
  state.data.meetings.isUpdatingMeetingUserPreferences;

export const getMeetingCreationStatus = (state: RootState) => state.data.meetings.meetingCreationStatus;

export const getMeetingInviteeSearchStatus = (state: RootState) => state.data.meetings.inviteeSearchLoadingStatus;

export const getSelectedMeetingAttendeeGroupId = (state: RootState) =>
  state.data.meetings.selectedMeetingAttendeeGroupId;

export const getUserClosedConsentTile = (state: RootState) => state.data.meetings.userPreferences.userClosedConsentTile;

export const getIsDeleting = (state: RootState) => state.data.meetings.isDeleting;

export const getMeetingUpdateLoadingStatus = (state: RootState) => state.data.meetings.meetingUpdateLoadingStatus;

export const {
  selectById: getMeetingById,
  selectIds: getMeetingIds,
  selectEntities: getMeetingEntities,
  selectAll: getAllMeetings,
  selectTotal: getTotalMeetings,
} = meetingsAdapter.getSelectors((state: RootState) => state.data.meetings.items);

export const {
  selectById: getMeetingSuggestionById,
  selectIds: getMeetingSuggestionIds,
  selectEntities: getMeetingSuggestionEntities,
  selectAll: getAllMeetingSuggestions,
  selectTotal: getTotalMeetingSuggestions,
} = suggestionsAdapter.getSelectors((state: RootState) => state.data.meetings.suggestions);

export const {selectAll: getAllTimeZones, selectById: getTimeZoneById} = timeZonesAdapter.getSelectors(
  (state: RootState) => state.data.meetings.timeZones,
);

export const getAreTimeZonesLoading = (state: RootState) => state.data.meetings.areTimeZonesLoading;

export const getSelectedMeeting = createSelector(getMeetingEntities, getSelectedMeetingId, (entities, id) => {
  if (id) {
    return entities[id];
  }
});

export const getAttendeesByMeetingId = createCachedSelector(
  getMeetingById,
  (meeting) => meeting?.attendees ?? [],
)((_state, meetingId) => meetingId);

export const getAttendeesBySelectedMeeting = createSelector(getSelectedMeeting, (meeting) => meeting?.attendees ?? []);

export const getLoggedInUserAsAttendee = createSelector(
  getAttendeesBySelectedMeeting,
  getUserDirectoryObjectId,
  (meetingAttendees, userDirectoryObjectId) => {
    return findAttendeeByDirectoryObjectId(meetingAttendees, userDirectoryObjectId);
  },
);

export const getAcceptingAttendeesByMeetingId = createCachedSelector(getAttendeesByMeetingId, (meetingAttendees) => {
  return meetingAttendees.filter(
    (attendee) => attendee.responseStatus === 'Accepted' || attendee.responseStatus === 'Organizer',
  );
})((_state, meetingId) => meetingId);

export const getOrganizerAttendeeByMeetingId = createCachedSelector(getAttendeesByMeetingId, (meetingAttendees) => {
  return meetingAttendees.find((attendee) => attendee.responseStatus === 'Organizer');
})((_state, meetingId) => meetingId);

export const getLoggedInUserResponseStatusByMeetingId = createCachedSelector(
  getAttendeesByMeetingId,
  getUserDirectoryObjectId,
  (attendees, userDirectoryObjectId) => {
    const currentAttendee = findAttendeeByDirectoryObjectId(attendees, userDirectoryObjectId);

    return currentAttendee?.responseStatus ?? 'Pending';
  },
)((_state, meetingId) => meetingId);

export const getMeetingsByDate = createCachedSelector(
  getAllMeetings,
  (_state: RootState, date: Date | undefined) => date,
  (meetings, date) => {
    if (!date) {
      return [];
    }

    return filterMeetingsByDate(meetings, date).sort(
      (a, b) =>
        dateCompareAsc(parseISO(a.startDateTime), parseISO(b.startDateTime)) ||
        dateCompareDesc(parseISO(a.endDateTime), parseISO(b.endDateTime)),
    );
  },
)((_state, date) => date?.toISOString() ?? '');

export const getPageTimeSpanInDays = (state: RootState) => state.data.meetings.settings.pageTimespanInDays;

export const getMeetingsLoadingStatusByDate = createCachedSelector(
  (state: RootState) => state.data.meetings.meetingsLoadingStatus,
  (_state: RootState, date: Date | undefined) => date,
  (meetingsLoadingStatus, date) => {
    if (!date) {
      return;
    }
    return meetingsLoadingStatus[formatDateAsKey(date)] ?? 'NotLoaded';
  },
)((_state, date) => date?.toISOString() || '');

export const getIsLoadingMeetingsByDate = createCachedSelector(
  getMeetingsLoadingStatusByDate,
  (_state: RootState, date: Date | undefined) => date,
  (meetingsLoadingStatus) => meetingsLoadingStatus === 'Loading',
)((_state, date) => date?.toISOString() || '');

export const getIsLoadingAnyMeetings = createSelector(
  (state: RootState) => state.data.meetings.meetingsLoadingStatus,
  (meetingLoadingStatuses) => {
    const loadingStatuses = Object.values(meetingLoadingStatuses);
    return loadingStatuses.includes('Loading');
  },
);

export const getHasMeetingPreconditions = (state: RootState) => isHybridMeetingsFeatureEnabled(state);

export const getIsUpdatingCalendarConsent = (state: RootState) => state.data.meetings.isUpdatingCalendarConsent;

export const getInviteeSearchResults = (state: RootState) => state.data.meetings.meetingInviteeSearchResults;

export const getAttendeeAvailabilityLoadingStatus = (state: RootState) =>
  state.data.meetings.attendeeAvailabilityLoadingStatus;

export const getMeetingCountOnDate = createCachedSelector(
  getAllMeetings,
  (_state: RootState, date: Date) => date,
  (allMeetings, date) => filterMeetingsByDate(allMeetings, date).length,
)((_state, date) => date.toISOString());

export const getAttendeesCountOnLocationByMeetingId = createCachedSelector(
  (_: RootState, _meetingId: string, date: string) => date,
  getAcceptingAttendeesByMeetingId,
  (date, meetingAttendees) => {
    return groupAndSortAttendeesByLocationType(meetingAttendees, date);
  },
)((_state, meetingId) => meetingId);

export const getIsUserOrganizerForMeeting = createCachedSelector(
  getMeetingById,
  getUserDirectoryObjectId,
  (meeting, userDirectoryObjectId) => {
    const {attendees} = meeting ?? {};

    const userAsAttendee = attendees?.find((attendee) => attendee.directoryObjectId === userDirectoryObjectId);
    return userAsAttendee?.responseStatus === 'Organizer';
  },
)((_state: RootState, id) => id.toString());

export const getCanUserAddRoomToExistingMeeting = createSelector(
  getMeetingById,
  getIsUserOrganizerForMeeting,
  (meeting, isUserMeetingOrganizer) => {
    const {isCanceled, startDateTime, endDateTime} = meeting ?? {};
    return (
      isUserMeetingOrganizer &&
      !isCanceled &&
      startDateTime &&
      endDateTime &&
      isMeetingDateTimeRangeValid(parseISO(startDateTime), parseISO(endDateTime))
    );
  },
);

export const getDisplayCalendarSyncTile = createSelector(
  getHasUserGivenCalendarConsent,
  getIsMeetingsCalendarEnabled,
  getUserClosedConsentTile,
  getIsOccupancyEnabled,
  getIsHybridMeetingsFeatureEnabled,
  (calendarConsent, meetingsCalendarEnabled, userClosedConsentTile, isOccupancyEnabled, hybridMeetingsFeatureEnabled) =>
    // Show the tile if the user has not given consent or the user has not closed the tile and the calendar is enabled
    meetingsCalendarEnabled &&
    !calendarConsent &&
    !userClosedConsentTile &&
    isOccupancyEnabled &&
    hybridMeetingsFeatureEnabled,
);
